import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  TextField,
  SwitchField,
  TextAreaField,
  SelectField,
  Badge,
  Text,
  Label,
  Message,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import { useMedicalConditionQuery } from "./api/useMedicalConditionQuery";
import { useMedicalConditionPutMutation } from "./api/useMedicalConditionMutation";
import { formatDate } from "../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserQuery } from "../common/useUserQuery";
import { TagsInput } from "react-tag-input-component";
import { putMedicalConditionSchema } from "./validations/medicalConditions/putValidation";

export default function MedicalConditionEditPage() {
  const navigate = useNavigate();
  const params = useParams();
  const medicalConditionId = params.id;
  const { data: userQueryData } = useUserQuery();

  const { isLoading, error, data } = useMedicalConditionQuery(
    userQueryData?.patientUserId,
    medicalConditionId
  );
  const [showDateInput, setShowDateInput] = useState(false);

  const {
    mutate: putMedicalConditionMutate,
    isLoading: putIsLoading,
    isSuccess: putIsSuccess,
    error: putError,
  } = useMedicalConditionPutMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(putMedicalConditionSchema),
  });

  useEffect(() => {
    if (data) {
      reset({
        diagnosisName: data?.diagnosisName,
        diagnosisDescription: data?.diagnosisDescription,
        tags: JSON.parse(data?.tags),
        medicalConditionStage: data?.medicalConditionStage,
        startDate: data?.startDate,
      });
      if (data?.medicalConditionStage === "DIAGNOSED") setShowDateInput(true);
    }
  }, [data]);

  useEffect(() => {
    if (putIsSuccess) {
      navigate("/user/hr/medical-problems/");
    }
  }, [putIsSuccess]);

  const handleOnSubmit = (values) => {
    try {
      const formatStartDate = !!values?.startDate
        ? formatDate(new Date(values.startDate))
        : null;

      let mcParams = {
        id: medicalConditionId,
        diagnosisName: values.diagnosisName,
        diagnosisDescription: values.diagnosisDescription,
        tags: values.tags,
        medicalConditionStage: values.medicalConditionStage,
        startDate: formatStartDate,
      };

      putMedicalConditionMutate(mcParams);
    } catch (error) {
      console.log("Error : ", error);
    }
  };

  if (isLoading) return "Loading...";

  if (error) return "An error has occurred: " + error.message;

  if (data)
    return (
      <>
        {putIsLoading && (
          <Flex direction={"column"} margin={15}>
            <Message isDismissible variation="filled" colorTheme="info">
              Saving in progress. Please wait...
            </Message>
          </Flex>
        )}
        {putError && (
          <Flex direction={"column"} margin={15}>
            <Message isDismissible variation="filled" colorTheme="error">
              There was an error saving your data. Please try again.
            </Message>
          </Flex>
        )}
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <FlexFormContainer headerTitle={"Medical Condition"}>
            <Controller
              name="diagnosisName"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Medical Condition Name"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.diagnosisName?.message}
                  hasError={!!errors.diagnosisName}
                />
              )}
            />
            <Controller
              name="diagnosisDescription"
              control={control}
              render={({ field }) => (
                <TextAreaField
                  minWidth="300px"
                  label="Medical Condition Description"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.diagnosisDescription?.message}
                  hasError={!!errors.diagnosisDescription}
                />
              )}
            />
            <Flex direction={"column"} gap={6}>
              <Label htmlFor="tags">Add Symptoms</Label>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => {
                  return (
                    <TagsInput
                      value={field?.value || []}
                      onChange={(tags) => field.onChange(tags)}
                    />
                  );
                }}
              />
            </Flex>
            <Controller
              name="medicalConditionStage"
              control={control}
              render={({ field }) => (
                <SelectField
                  minWidth="300px"
                  label="Medical Condition Stage"
                  placeholder="Select"
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    if (value === "DIAGNOSED") setShowDateInput(true);
                    else {
                      setShowDateInput(false);
                      setValue("startDate", "");
                    }
                  }}
                  options={["DIAGNOSED", "POTENTIAL", "PRESUMED", "RULED OUT"]}
                  errorMessage={errors.medicalConditionStage?.message}
                  hasError={!!errors.medicalConditionStage}
                ></SelectField>
              )}
            />
            {showDateInput && (
              <>
                <Label htmlFor="startDate">Start Date</Label>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <>
                      <DatePicker
                        id={field.name}
                        onChange={(value) => field.onChange(value)}
                        value={
                          field.value != null ? new Date(field.value) : null
                        }
                        format="yyyy-MM-dd"
                        oneTap
                        size="lg"
                        style={{ width: "300px" }}
                      />
                      {!!errors.startDate && (
                        <Text
                          variation="error"
                          as="p"
                          lineHeight="0.5em"
                          fontWeight={400}
                          fontSize="0.8em"
                          fontStyle="normal"
                          textDecoration="none"
                          width="30vw"
                          style={{ marginLeft: "10px" }}
                        >
                          {errors.startDate.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </>
            )}

            <Flex gap="12" direction="column">
              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
}
